/**
 * Pure helpers shared by the Absence Compliance screens (no React, no network), so they can be
 * unit-tested. Requirements and decisions: docs/absence-compliance/ABSENCE_COMPLIANCE_PLAN.md
 */
import moment from "moment";

export const GO_LIVE_NOTICE =
  "Pending Absence has not started yet. The list will appear after the go-live date.";

/** Colours per status. Also used by the attendance calendar. Provisional: design may refine them. */
export const STATUS_META = {
  PENDING: { label: "Pending Absence", color: "#f8ac59" },
  REFERRED_BACK: { label: "Referred Back", color: "#e67e22" },
  CORRECTION_REQUESTED: { label: "Correction Requested", color: "#8e6bbf" },
  AUTHORIZED_ABSENCE: { label: "Authorized Absence", color: "#a0522d" },
  UNAUTHORIZED_ABSENCE: { label: "Unauthorized Absence", color: "#b03a2e" },
  APPROVED_PRESENCE: { label: "Approved Presence", color: "#23c6c8" },
  HR_RESOLVED_PRESENCE: { label: "HR Resolved Presence", color: "#23c6c8" },
};

/**
 * Shown to staff and approvers instead of the stored status once a day is overdue (the server sets
 * `shows_as_absence`). Display only: HR still sees the real status and can act on the day.
 */
const OVERDUE_ABSENCE_META = { label: "Absence (deadline passed)", color: "#5d5d5a" };

/** The status a staff/approver screen should show for an item. */
export function displayStatus(item) {
  return item && item.shows_as_absence ? "OVERDUE_ABSENCE" : item && item.status;
}

/**
 * The attendance calendar shows only two absence colours (owner decision): "pending" for every day
 * that is not settled yet (Pending, Referred Back, Correction Requested, and Authorized Absence,
 * which still waits for the leave) and "absence" for the final Unauthorized Absence. The exact
 * status stays in the day's tooltip and in the Pending Absence list.
 */
export const CALENDAR_PENDING_STATUSES = ["PENDING", "REFERRED_BACK", "CORRECTION_REQUESTED", "AUTHORIZED_ABSENCE"];
export const CALENDAR_ABSENCE_STATUSES = ["UNAUTHORIZED_ABSENCE"];

/** 'absence' (final, including an overdue day), 'pending', or null (not drawn as an absence). */
export function calendarGroup(item) {
  if (!item) return null;
  if (item.shows_as_absence || CALENDAR_ABSENCE_STATUSES.includes(item.status)) return "absence";
  return CALENDAR_PENDING_STATUSES.includes(item.status) ? "pending" : null;
}

/**
 * Splits the server's legacy absence count into final Absence days and Pending Absence days.
 * Pending days are the legacy "Absence" days that still have an unsettled pending-absence item, so
 * the two counts always add up to the original absence count.
 */
export function splitAbsenceCounts({ absenceCount = 0, finalData = [], pendingAbsences = [] } = {}) {
  const pendingDates = new Set(
    pendingAbsences.filter((item) => calendarGroup(item) === "pending").map((item) => item.date)
  );
  const pendingCount = new Set(
    finalData
      .filter((item) => item && item.attendanceStatus === "Absence" && pendingDates.has(item.date))
      .map((item) => item.date)
  ).size;
  return { absenceCount: Math.max(0, absenceCount - pendingCount), pendingCount };
}

export const OPEN_STATUSES = ["PENDING", "REFERRED_BACK", "CORRECTION_REQUESTED"];

/** Awareness message shown above the employee's list (Myanmar text from the SRS comment). */
export const AWARENESS_TEXT = {
  en:
    "If a day shows as Absence, it is treated as a day you did not come to the office. " +
    "If you did come to the office, you can submit a Correction so that the day is counted as Presence. " +
    "If you were on leave, applying for the leave in the Leave module removes the day from your Absence list.",
  my:
    "ဝန်ထမ်းသည် Absence ဖြစ်နေပါက ရုံးမတက်ဘဲ ပျက်ကွက်သည်ဟု ယူဆပါသည်။ " +
    "ဝန်ထမ်းသည် ရုံးတက်ပါက Presence(ရုံးတက်)စာရင်းတွင် ပါရှိရန် ပြန်လည်ပြင်ဆင်ခွင့် (Correction) ရှိပါသည်။ " +
    "ဝန်ထမ်းသည် ခွင့်ယူထားပါက ခွင့်တင်ခြင်းအားဖြင့် Absence (ပျက်ကွက်)စာရင်းတွင် ပါရှိမည် မဟုတ်တော့ပါ။",
};

export function statusLabel(status) {
  if (status === "OVERDUE_ABSENCE") return OVERDUE_ABSENCE_META.label;
  return (STATUS_META[status] || { label: status }).label;
}

export function statusColor(status) {
  if (status === "OVERDUE_ABSENCE") return OVERDUE_ABSENCE_META.color;
  return (STATUS_META[status] || { color: "#999999" }).color;
}

/** The date shown everywhere in this feature: 'YYYY-MM-DD' (owner decision). */
export function formatDate(dateString) {
  if (!dateString) return "";
  const parsed = moment(dateString, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : dateString;
}

/** 'HH:mm' from 'HH:mm:ss'. */
export function formatTime(timeString) {
  return timeString ? String(timeString).slice(0, 5) : "";
}

/**
 * The countdown text for one item, from the API fields deadline_date / days_left / is_overdue.
 * tone: 'none' (no deadline entered), 'ok', 'soon' (2 days or fewer), 'overdue'.
 */
export function describeDeadline(item) {
  if (!item || !item.deadline_date) return { text: "", tone: "none" };
  const date = formatDate(item.deadline_date);
  if (item.is_overdue) {
    return { text: `Deadline passed (${date}). HR will resolve this.`, tone: "overdue" };
  }
  if (item.days_left === 0) {
    return { text: `Last day today. Must be resolved by ${date}.`, tone: "soon" };
  }
  const days = `${item.days_left} day${item.days_left === 1 ? "" : "s"} left`;
  return {
    text: `${days}. Must be resolved by ${date}.`,
    tone: item.days_left <= 2 ? "soon" : "ok",
  };
}

/** Stored status codes (tbl_pending_absence_action.status) -> status names. null = still plain pending. */
const STATUS_CODE_NAMES = {
  1: "CORRECTION_REQUESTED",
  2: "REFERRED_BACK",
  3: "APPROVED_PRESENCE",
  4: "HR_RESOLVED_PRESENCE",
  5: "AUTHORIZED_ABSENCE",
  6: "UNAUTHORIZED_ABSENCE",
};

/** Audit action codes (tbl_pending_absence_log.action_code). */
const LOG_ACTION_LABELS = {
  1: "Correction submitted",
  2: "Correction resubmitted",
  3: "Approved",
  4: "Referred back",
  5: "HR resolved presence",
  6: "Marked Authorized Absence",
  7: "Marked Unauthorized Absence",
};

export function describeStatusCode(code) {
  if (code === null || code === undefined) return statusLabel("PENDING");
  return statusLabel(STATUS_CODE_NAMES[Number(code)]);
}

export function describeLogAction(code) {
  return LOG_ACTION_LABELS[Number(code)] || `Action ${code}`;
}

/** Days HR can still act on: everything except days already resolved to presence. */
export function canHrAct(item) {
  return item.status !== "APPROVED_PRESENCE" && item.status !== "HR_RESOLVED_PRESENCE";
}

export const itemKey = (item) => `${item.user_id}|${item.absence_date}`;

/** Keys of the items that can be selected (the predicate decides). */
export function selectableKeys(items, canSelect) {
  return items.filter(canSelect).map(itemKey);
}

export function toggleKey(selected, key) {
  const next = new Set(selected);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** True only when there is something selectable and all of it is selected. */
export function isAllSelected(items, selected, canSelect) {
  const keys = selectableKeys(items, canSelect);
  return keys.length > 0 && keys.every((key) => selected.has(key));
}

/** Select every selectable item, or clear the selection when everything is already selected. */
export function toggleAll(items, selected, canSelect) {
  if (isAllSelected(items, selected, canSelect)) return new Set();
  return new Set(selectableKeys(items, canSelect));
}

/** The { user_id, absence_date } list the API expects for the selected keys. */
export function toRequestItems(items, selected) {
  return items
    .filter((item) => selected.has(itemKey(item)))
    .map((item) => {
      const entry = { user_id: item.user_id, absence_date: item.absence_date };
      // The correction on screen. An approval is refused if the employee has resent it since.
      if (item.request && item.request.requested_date) entry.requested_date = item.request.requested_date;
      return entry;
    });
}

/** Summary of a bulk response for a toast: counts plus the first few failure messages. */
export function summarizeResults(data) {
  const results = (data && data.results) || [];
  const failures = results
    .filter((result) => !result.ok)
    .map((result) => `${formatDate(result.absence_date)}: ${result.message}`);
  return {
    succeeded: results.filter((result) => result.ok).length,
    failed: failures.length,
    failures,
  };
}

/** Where the Leave form should open for an absence date (it reads absence_date from the URL). */
export function buildLeaveUrl(absenceDate) {
  return `/new_leave?absence_date=${encodeURIComponent(absenceDate)}`;
}

/**
 * One entry per employee (first wins). The server returns each person once, but a repeated
 * `user_id` would give React two rows with the same key, which can leave stale rows on screen when
 * a filter changes, so the pages never trust it.
 */
export function uniqueEmployees(list) {
  const seen = new Set();
  return (list || []).filter((employee) => {
    if (!employee || seen.has(employee.user_id)) return false;
    seen.add(employee.user_id);
    return true;
  });
}

const ALL_OPTION = { value: 0, label: "All" };

const idOf = (option) => (option && option.value ? Number(option.value) : 0);

/** Unique { value, label } pairs from the employees, sorted by label. */
function uniqueOptions(employees, idKey, labelKey) {
  const seen = new Map();
  employees.forEach((employee) => {
    const id = employee[idKey];
    if (id && !seen.has(id)) seen.set(id, { value: id, label: employee[labelKey] || String(id) });
  });
  return [...seen.values()].sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

/**
 * Options of the five HR filter dropdowns, narrowed by what is already selected. Built from the
 * active employees (the same people the list is made of), so an option with nobody in it is
 * never offered. Two chains: Region -> Branch and Department -> Designation; Employee is narrowed
 * by all four. Region and Department are independent of each other.
 */
export function buildCascadeOptions(employees, filters) {
  const people = employees || [];
  const regionId = idOf(filters && filters.region);
  const branchId = idOf(filters && filters.branch);
  const departmentId = idOf(filters && filters.department);
  const designationId = idOf(filters && filters.designation);

  const inRegion = (e) => !regionId || e.region_id === regionId;
  const inDepartment = (e) => !departmentId || e.department_id === departmentId;

  const withAll = (options) => [ALL_OPTION, ...options];
  return {
    region: withAll(uniqueOptions(people, "region_id", "region")),
    branch: withAll(uniqueOptions(people.filter(inRegion), "branch_id", "branch")),
    department: withAll(uniqueOptions(people, "department_id", "department")),
    designation: withAll(uniqueOptions(people.filter(inDepartment), "designation_id", "designation")),
    employee: withAll(
      people
        .filter(
          (e) =>
            inRegion(e) &&
            inDepartment(e) &&
            (!branchId || e.branch_id === branchId) &&
            (!designationId || e.designation_id === designationId)
        )
        .map((e) => ({ value: e.user_id, label: e.display_name }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label)))
    ),
  };
}

/**
 * After a filter changed: any lower selection that no longer fits the narrowed options goes back
 * to All (e.g. the branch is in another region than the one just chosen).
 */
export function reconcileFilters(filters, employees) {
  const options = buildCascadeOptions(employees, filters);
  const next = { ...filters };
  ["branch", "designation", "employee"].forEach((key) => {
    const selected = idOf(filters[key]);
    if (selected && !options[key].some((option) => option.value === selected)) next[key] = ALL_OPTION;
  });
  return next;
}

/* ------------------------- share a reminder (Telegram, Viber, copy) ------------------------- */

const MAX_DAYS_IN_MESSAGE = 10;

/** The days a reminder is about: this employee's listed days that still wait for the employee. */
export function nudgeDays(items, userId) {
  return (items || [])
    .filter((item) => Number(item.user_id) === Number(userId) && item.can_employee_act)
    .sort((a, b) => a.absence_date.localeCompare(b.absence_date));
}

/**
 * The text sent through a chat app: plain facts, neutral enough to post in a group. Only the
 * employee, the pending days and the deadline: no reasons, comments or leave balance (it leaves
 * HRM once it is shared).
 */
export function buildNudgeMessage(displayName, days) {
  if (!days || days.length === 0) return "";
  const shown = days.slice(0, MAX_DAYS_IN_MESSAGE).map((d) => formatDate(d.absence_date));
  const extra = days.length - shown.length;
  const list = shown.join(", ") + (extra > 0 ? ` and ${extra} more` : "");
  const deadlines = days.filter((d) => d.deadline_date && !d.is_overdue).map((d) => d.deadline_date).sort();

  return [
    "Pending Absence",
    `Employee: ${displayName || "-"}`,
    `Pending days (${days.length}): ${list}`,
    ...(deadlines.length > 0 ? [`Deadline: ${formatDate(deadlines[0])}`] : []),
  ].join("\n");
}

/**
 * Links that open Telegram / Viber with the text ready; the person picks who to send it to.
 * Telegram's share link refuses to open without its `url` parameter (checked against t.me), so the
 * whole text goes there; Telegram puts it in the message box as it is.
 */
export function buildShareLinks(message) {
  return {
    telegram: `https://t.me/share/url?url=${encodeURIComponent(message)}`,
    viber: `viber://forward?text=${encodeURIComponent(message)}`,
  };
}

/** Copies text to the clipboard. Resolves true on success. */
export async function copyText(text) {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    // fall through to the older way
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const done = document.execCommand("copy");
    document.body.removeChild(area);
    return Boolean(done);
  } catch (error) {
    return false;
  }
}

/**
 * Rows for the Excel export of the exemption register: only the exemptions that are active today
 * (covering today), one row each, with the name and the employee code as separate columns.
 */
export function buildExemptionRows(exemptions) {
  return (exemptions || [])
    .filter((exemption) => exemption.state === "ACTIVE")
    .map((exemption) => ({
      Name: exemption.fullname || "",
      "Employee Code": exemption.employment_id || "",
      Designation: exemption.designation || "",
      Department: exemption.department || "",
      Branch: exemption.branch || "",
      Region: exemption.region || "",
      "Start Date": formatDate(exemption.start_date),
      "End Date": exemption.is_unlimited ? "Unlimited" : formatDate(exemption.end_date),
      "Registered On": formatDate(String(exemption.created_date || "").slice(0, 10)),
    }));
}

/** Rows for the Excel report (one object per item, column names as headings). */
export function buildReportRows(items) {
  return items.map((item) => ({
    Name: item.fullname || "",
    "Employee Code": item.employment_id || "",
    Designation: item.designation || "",
    Department: item.department || "",
    Branch: item.branch || "",
    Region: item.region || "",
    "Absence Date": item.absence_date,
    Status: statusLabel(item.status),
    "Employee Reason": item.request ? item.request.reason : "",
    "Approver / HR Comment": item.comment || "",
    "Resolved By Name": item.resolved_by_fullname || "",
    "Resolved By Code": item.resolved_by_employment_id || "",
    "Resolved Date": item.resolved_date || "",
    Deadline: item.deadline_date || "",
    Overdue: item.is_overdue ? "Yes" : "No",
  }));
}
